import { api as wiredApi } from './index';

/**
 * What InGo's Settings make public, before or after anyone signs in
 * (armkit/settings, `GET public/settings` and `GET public/legal`): the
 * office's contact line for the help screens (Rio, 2 Oct 2026: support chat
 * becomes contact buttons), the countries InGo operates in and its home
 * (InGo ADR 0037: the sign-up pages' country, the WhatsApp link's code), and
 * the terms and privacy texts. Each asked once a session; a failed ask is
 * asked again next time.
 */
export function createPublicSettings({ api }) {
  let answer = null;
  let documents = null;

  const settings = () => {
    answer ??= api.get('/public/settings').then(
      ({ data }) => data ?? {},
      (error) => {
        answer = null;
        throw error;
      },
    );

    return answer;
  };

  return {
    /**
     * `{ appName, phone, email, dialCode }` from Settings, Branding; null where
     * it is not set. `dialCode` is the home country's ("+263"), what a number
     * written locally is dialled with.
     */
    async contacts() {
      const data = await settings();
      const b = data?.settings?.branding ?? {};

      return {
        appName: b.app_name || 'InGo',
        phone: text(b.support_phone),
        email: text(b.contact_email),
        dialCode: regionFrom(data?.region)?.home.dialCode ?? null,
      };
    },

    /**
     * `{ home, countries }` from Settings, Regional: the home country and every
     * country InGo operates in, each `{ code, name, dialCode, phoneExample }`.
     * Null when the API names none.
     */
    async region() {
      return regionFrom((await settings())?.region);
    },

    /** `{ terms, privacy }` from Settings, Terms and privacy; null where the operator wrote none. */
    async legal() {
      documents ??= api.get('/public/legal').then(
        ({ data }) => data?.documents?.legal ?? {},
        (error) => {
          documents = null;
          throw error;
        },
      );
      const d = await documents;

      return { terms: text(d.terms), privacy: text(d.privacy) };
    },
  };
}

function text(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function country(row) {
  const code = text(row?.code);
  const dialCode = text(row?.dial_code);

  return code && dialCode ? { code, name: text(row.name) || code, dialCode, phoneExample: text(row.phone_example) || '' } : null;
}

function regionFrom(region) {
  const home = country(region?.home);
  if (!home) return null;
  const countries = (Array.isArray(region.countries) ? region.countries : []).map(country).filter(Boolean);

  return { home, countries: countries.length ? countries : [home] };
}

export const publicSettings = createPublicSettings({ api: wiredApi });

/**
 * A phone number as WhatsApp's link wants it: digits only, with the country's
 * code. Written locally, it is the home country's (`dialCode`, "+263"): "0789
 * 701 394" is 263789701394; "+27 82 …" keeps its own code. Null when there is
 * nothing that looks like a number, or a local one and no home to dial it in.
 */
export function whatsAppNumber(phone, dialCode) {
  const written = String(phone || '').trim();
  const digits = written.replace(/\D/g, '');
  if (digits.length < 7) return null;
  if (written.startsWith('+')) return digits;
  if (digits.startsWith('00')) return digits.slice(2);
  if (digits.startsWith('0')) {
    const code = String(dialCode || '').replace(/\D/g, '');

    return code ? code + digits.slice(1) : null;
  }

  return digits;
}
